import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import TopBar from '@/components/TopBar';
import { healthClassToColor } from '@/lib/utils';
import { CardSm } from '@/components/ui/Form';

// Fix default Leaflet icon path issue when bundled
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

export default function MapView() {
  const [plots, setPlots] = useState([]);
  const [trees, setTrees] = useState([]);
  const [center, setCenter] = useState([21.9497, 89.1833]);
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    (async () => {
      const [p, t] = await Promise.all([api.get('/plots'), api.get('/trees')]);
      setPlots(p.data);
      setTrees(t.data);
      if (t.data.length > 0) {
        setCenter([t.data[0].gps_lat, t.data[0].gps_lon]);
        setHasData(true);
      } else if (p.data.length > 0) {
        setCenter([p.data[0].centre_lat, p.data[0].centre_lon]);
        setHasData(true);
      }
    })();
  }, []);

  return (
    <div data-testid="map-page">
      <TopBar title="Plot Map" />
      <div className="px-4 pt-3">
        {!hasData && (
          <CardSm className="mb-3" data-testid="map-empty">
            <p className="text-sm text-carbon-soft">
              No plots yet — map shows demo coordinates. Register trees or create a plot to populate.
            </p>
          </CardSm>
        )}
      </div>
      <div className="relative w-full" style={{ height: 'calc(100dvh - 240px)' }}>
        <MapContainer
          center={center}
          zoom={hasData ? 17 : 6}
          style={{ width: '100%', height: '100%', border: '2px solid #1A1D1A' }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {plots.map((p) => (
            <Circle
              key={p.id}
              center={[p.centre_lat, p.centre_lon]}
              radius={Math.sqrt(p.area_m2 / Math.PI)}
              pathOptions={{ color: '#2D6A4F', weight: 2, fillOpacity: 0.1 }}
            >
              <Popup>
                <div className="font-display font-bold">{p.plot_code}</div>
                <div className="text-xs">{p.area_m2} m² · {p.stratum}</div>
              </Popup>
            </Circle>
          ))}
          {trees.map((t) => (
            <CircleMarker
              key={t.id}
              center={[t.gps_lat, t.gps_lon]}
              radius={8}
              pathOptions={{
                color: '#1A1D1A',
                weight: 2,
                fillColor: healthClassToColor(t.latest_health_class || 'Healthy'),
                fillOpacity: 1,
              }}
            >
              <Popup>
                <div className="font-display font-bold">{t.tree_code}</div>
                <div className="text-xs italic">{t.species_scientific}</div>
                <div className="text-xs">Health: {t.latest_health_class || '—'}</div>
                <Link to={`/trees/${t.id}`} className="underline font-bold text-sm">
                  Open tree →
                </Link>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>
      <div className="p-4 grid grid-cols-2 gap-2">
        <Legend color="#059669" label="Healthy" />
        <Legend color="#D97706" label="Mild stress" />
        <Legend color="#EA580C" label="Moderate" />
        <Legend color="#DC2626" label="Severe / dead" />
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <div className="flex items-center gap-2 text-xs font-display font-bold uppercase tracking-widest">
      <span className="w-3 h-3 border-2 border-carbon" style={{ background: color }} />
      {label}
    </div>
  );
}
